import { useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import type { ColumnDef } from '@tanstack/react-table'
import { Ban, Layers, Plus } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
  costCenterKeys,
  costCentersQueryOptions,
  deleteCostCenter,
} from '@/features/cost-centers/api'
import { CostCenterFormDrawer } from '@/features/cost-centers/cost-center-form-drawer'
import {
  CENTROS_ORDEM_DEFAULT,
  centrosSearchSchema,
  toCostCenterListParams,
  type CentrosSearch,
} from '@/features/cost-centers/search'
import type { CostCenterOut } from '@/shared/api/types'
import { Badge } from '@/shared/ui/badge'
import { Button } from '@/shared/ui/button'
import { EmptyState } from '@/shared/ui/empty-state'
import { PageHeader } from '@/widgets/app-shell/page-header'
import { DataTable } from '@/widgets/data-table/data-table'
import { NoResultsState } from '@/widgets/data-table/no-results-state'
import { RowDeleteButton } from '@/widgets/data-table/row-delete-button'
import { RowEditButton } from '@/widgets/data-table/row-edit-button'
import { TablePager } from '@/widgets/data-table/table-pager'
import { fromSortingState, toSortingState } from '@/widgets/data-table/table-search'
import { TableToolbar } from '@/widgets/data-table/table-toolbar'

export const Route = createFileRoute('/_authed/configuracoes/centros-de-custo')({
  validateSearch: centrosSearchSchema,
  loaderDeps: ({ search }) => ({ params: toCostCenterListParams(search) }),
  loader: ({ context, deps }) =>
    context.queryClient.ensureQueryData(costCentersQueryOptions(deps.params)),
  component: CentrosDeCustoPage,
})

function CentrosDeCustoPage() {
  const { t } = useTranslation(['costCenters', 'nav', 'common'])
  const search = Route.useSearch()
  const navigate = Route.useNavigate()
  const [drawerAberto, setDrawerAberto] = useState(false)
  // null = criar; centro = editar. Alimenta a `key` do drawer (remonta o form).
  const [emEdicao, setEmEdicao] = useState<CostCenterOut | null>(null)
  const { data, isLoading, isFetching, error, refetch } = useQuery(
    costCentersQueryOptions(toCostCenterListParams(search)),
  )

  const aplicar = (patch: Partial<CentrosSearch>) => {
    // `to` EXPLÍCITO + `prev` ANOTADO: sem os dois o destino fica ambíguo, o
    // updater cai em `any` (TS7006) e a navegação pode virar no-op silencioso.
    void navigate({
      to: '/configuracoes/centros-de-custo',
      search: (prev: CentrosSearch) => ({ ...prev, ...patch }),
      replace: true,
    })
  }
  /** Filtrar/ordenar volta para a página 1 — senão abre num offset vazio. */
  const filtrar = (patch: Partial<CentrosSearch>) => {
    aplicar({ ...patch, pagina: 1 })
  }

  const columns = useMemo(
    () =>
      [
        // `name` é o único campo ordenável da allowlist do backend que a tabela
        // mostra; a coluna de ações não é dado.
        { accessorKey: 'name', header: t('costCenters:columnName'), enableSorting: true },
        {
          id: 'actions',
          header: () => null,
          cell: (c) => {
            const cc = c.row.original
            return (
              <div className="flex justify-end gap-1">
                <RowEditButton
                  ariaLabel={t('costCenters:editAria', { name: cc.name })}
                  onEdit={() => {
                    // setState são estáveis: dispensam entrar nas deps do useMemo.
                    setEmEdicao(cc)
                    setDrawerAberto(true)
                  }}
                />
                <RowDeleteButton
                  ariaLabel={t('common:actions.delete')}
                  confirmTitle={t('costCenters:deleteConfirm.title', { name: cc.name })}
                  confirmDescription={t('costCenters:deleteConfirm.description')}
                  successMessage={t('costCenters:deleted')}
                  onDelete={() => deleteCostCenter(cc.id)}
                  invalidateKey={costCenterKeys.lists()}
                  conflictMessages={{ cost_center_in_use: t('costCenters:errorInUse') }}
                />
              </div>
            )
          },
        },
      ] as ColumnDef<CostCenterOut>[],
    [t],
  )

  const abrirCriar = () => {
    setEmEdicao(null)
    setDrawerAberto(true)
  }

  const botaoNovo = (
    <Button variant="primary" onClick={abrirCriar}>
      <Plus aria-hidden />
      {t('costCenters:new')}
    </Button>
  )

  return (
    <>
      <PageHeader title={t('nav:settings.costCenters')} actions={botaoNovo} />
      <div className="flex flex-col gap-4 p-6">
        <TableToolbar
          inline
          busca={search.q}
          onBuscaChange={(q) => {
            filtrar({ q })
          }}
          buscaPlaceholder={t('costCenters:searchPlaceholder')}
          chips={
            search.q === undefined
              ? []
              : [
                  {
                    id: 'q',
                    label: `${t('costCenters:columnName')}: ${search.q}`,
                    onRemove: () => {
                      filtrar({ q: undefined })
                    },
                  },
                ]
          }
          onLimpar={() => {
            filtrar({ q: undefined })
          }}
        />

        <DataTable
          columns={columns}
          page={data}
          isLoading={isLoading}
          isFetching={isFetching}
          error={error}
          onRetry={() => void refetch()}
          getRowId={(r) => r.id}
          sorting={toSortingState(search.ordenar)}
          onSortingChange={(s) => {
            filtrar({ ordenar: fromSortingState(s, CENTROS_ORDEM_DEFAULT) })
          }}
          footer={
            <TablePager
              total={data?.total ?? 0}
              pagina={search.pagina}
              linhas={search.linhas}
              onPageChange={(pagina) => {
                aplicar({ pagina })
              }}
              onLinhasChange={(linhas) => {
                aplicar({ linhas, pagina: 1 })
              }}
            />
          }
          ariaLabel={t('costCenters:ariaTable')}
          empty={
            search.q !== undefined ? (
              <NoResultsState
                onLimpar={() => {
                  filtrar({ q: undefined })
                }}
              />
            ) : (
              <EmptyState
                icon={Layers}
                title={t('costCenters:empty.title')}
                description={t('costCenters:empty.description')}
                action={botaoNovo}
              />
            )
          }
        />

        {/* "Desabilitar centros de custo" é um flag de tenant que o back-end não
            expõe (não há endpoint de configuração da empresa). Fica como ação
            desabilitada "Em breve" — nunca desliga nada de fato. */}
        <div
          className="text-ink-subtle flex w-fit cursor-not-allowed items-center gap-1.5 text-sm"
          aria-disabled
          title={t('common:comingSoon.title')}
        >
          <Ban className="size-4 shrink-0" aria-hidden />
          <span>{t('costCenters:disableFeature')}</span>
          <Badge tone="soon">{t('common:comingSoon.badge')}</Badge>
        </div>
      </div>
      <CostCenterFormDrawer
        // key: remonta o form ao alternar criar↔editar/entre centros.
        key={emEdicao ? `edit-${emEdicao.id}` : 'new'}
        costCenter={emEdicao ?? undefined}
        open={drawerAberto}
        onOpenChange={(aberto) => {
          setDrawerAberto(aberto)
          if (!aberto) setEmEdicao(null)
        }}
      />
    </>
  )
}
